import React from 'react';
import designImage from '../assets/images/vill_terv.png';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaCheck, FaDraftingCompass, FaBuilding, FaClipboardCheck } from 'react-icons/fa';

const VillamosTervezes: React.FC = () => {
  const services = [
    {
      icon: <FaDraftingCompass className="text-3xl text-blue-600" />,
      title: "Engedélyezési tervek",
      description: "Hivatalos engedélyezési dokumentáció készítése minden projekt típushoz.",
      features: ["Építési engedély", "Használatbavételi engedély", "Elektromos tervek", "Szakhatósági egyeztetés"]
    },
    {
      icon: <FaBuilding className="text-3xl text-green-600" />,
      title: "Kivitelezési tervek",
      description: "Részletes kivitelezési dokumentáció a szakszerű megvalósításhoz.",
      features: ["Telepítési rajzok", "Kapcsolási rajzok", "Anyaglisták", "Műszaki leírások"]
    },
    {
      icon: <FaClipboardCheck className="text3xl text-orange-600" />,
      title: "Felülvizsgálatok",
      description: "Meglévő rendszerek átfogó felülvizsgálata és dokumentálása.",
      features: ["Villamos felülvizsgálat", "Szabványossági ellenőrzés", "Hibajavítási javaslatok", "Korszerűsítési tervek"]
    }
  ];

  const projectTypes = [
    {
      title: "Lakóépületek",
      description: "Családi házak, társasházak villamos tervezése",
      features: ["Világítás tervezés", "Dugaljzás", "Elosztótáblák", "Biztonsági rendszerek"]
    },
    {
      title: "Irodaépületek",
      description: "Üzleti ingatlanok komplex villamos rendszerei",
      features: ["Irodai világítás", "Informatikai hálózat", "Klímarendszer", "Biztonsági világítás"]
    },
    {
      title: "Ipari létesítmények",
      description: "Gyárak, üzemek villamos infrastruktúrája",
      features: ["Nagyáramú rendszerek", "Géptáplálás", "Vezérlőrendszerek", "Érintésvédelem"]
    }
  ];

  const advantages = [
    "Engedélyezési tervek",
    "Kivitelezési tervek",
    "Felülvizsgálatok",
    "Tanácsadás",
    "Műszaki támogatás",
    "Gyors átfutási idő"
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-96 bg-gradient-to-r from-blue-600 to-indigo-700 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={designImage} 
            alt="Villamos tervezés" 
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="relative z-10 container mx-auto px-4 h-full flex items-center">
          <div className="text-white max-w-2xl">
            <h1 className="text-4xl md:text-6xl font-bold mb-4">
              Villamos Tervezés
            </h1>
            <p className="text-xl md:text-2xl mb-6">
              Szakértő villamos tervezési szolgáltatások minden projekthez
            </p>
            <a 
              href="/#contact"
              className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Ajánlatot kérek
            </a>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">
            Tervezési szolgáltatások
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow text-center">
                <div className="mb-4">{service.icon}</div>
                <h3 className="text-xl font-bold mb-4 text-indigo-600">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <FaCheck className="text-indigo-600 mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Types Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">
            Projekt típusok
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {projectTypes.map((project, index) => (
              <div key={index} className="bg-indigo-50 rounded-xl p-6">
                <h3 className="text-xl font-bold mb-4 text-indigo-700">{project.title}</h3>
                <p className="text-gray-600 mb-4">{project.description}</p>
                <ul className="space-y-2">
                  {project.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <FaCheck className="text-indigo-600 mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-blue-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">
            Tervezési folyamat
          </h2>
          
          <div className="grid md:grid-cols-5 gap-6">
            {[
              { step: "1", title: "Konzultáció", desc: "Igények felmérése és helyszíni bejárás" },
              { step: "2", title: "Koncepció", desc: "Alapkoncepció és előzetes terv" },
              { step: "3", title: "Részletes tervezés", desc: "Engedélyezési és kivitelezési tervek" },
              { step: "4", title: "Egyeztetés", desc: "Szakhatóságokkal való egyeztetés" },
              { step: "5", title: "Átadás", desc: "Végleges tervek és dokumentáció átadása" }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-indigo-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-800">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Standards Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">
            Szabványok és előírások
          </h2>
          
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4 text-indigo-600">Magyar szabványok</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• MSZ 447 sorozat</li>
                  <li>• MSZ HD 60364 sorozat</li>
                  <li>• MSZ EN 61439 sorozat</li>
                  <li>• Építési termékek rendelet</li>
                </ul>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-4 text-indigo-600">Európai normák</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• IEC 60364 sorozat</li>
                  <li>• EN 50110 sorozat</li>
                  <li>• EN 61439 sorozat</li>
                  <li>• CPR rendelet</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="py-16 bg-indigo-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">
            Szolgáltatásaink előnyei
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {advantages.map((advantage, index) => (
              <div key={index} className="flex items-center bg-white p-4 rounded-lg shadow">
                <FaCheck className="text-indigo-600 mr-3 text-xl" />
                <span className="text-gray-800 font-medium">{advantage}</span>
              </div>
            ))}
          </div>
        </div>
      </section>



      {/* Contact Section */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12">
            Kérjen Árajánlatot!
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div>
              <h3 className="text-2xl font-semibold mb-6">Elérhetőségeink</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center text-white text-xl">
                    📞
                  </div>
                  <div>
                    <div className="font-medium">Nagy Péter Villanyszerelő mester</div>
                    <a 
                      href="tel:+36703620652"
                      className="text-red-400 hover:text-red-300 font-semibold transition-colors"
                    >
                      +36703620652
                    </a>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center text-white text-xl">
                    📞
                  </div>
                  <div>
                    <div className="font-medium">Tamasi Krisztán villamosbiztonsági felülvizsgáló</div>
                    <a 
                      href="tel:+36706049889"
                      className="text-red-400 hover:text-red-300 font-semibold transition-colors"
                    >
                      +36706049889
                    </a>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center text-white text-xl">
                    ✉️
                  </div>
                  <div>
                    <a 
                      href="mailto:kelectrokft@gmail.com"
                      className="hover:text-red-400 transition-colors"
                    >
                      kelectrokft@gmail.com
                    </a>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center text-white text-xl">
                    📍
                  </div>
                  <div>
                    <span>SZOLNOK és 100km-es körzete</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-800 p-6 rounded-xl">
              <h3 className="text-xl font-semibold mb-4">Gyors kapcsolatfelvétel</h3>
              <p className="text-gray-300 mb-4">
                Hívjon minket bizalommal! Ingyenes helyszíni felméréssel és 
                részletes árajánlattal várjuk megkeresését.
              </p>
              <div className="space-y-2">
                <p className="text-red-400 font-semibold">📞 Azonnali válasz</p>
                <p className="text-red-400 font-semibold">📋 Ingyenes felmérés</p>
                <p className="text-red-400 font-semibold">💰 Versenyképes árak</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VillamosTervezes; 